/**
 * Bindeglied fuer Stufe 2: aus dem, was gespeichert ist, wird die Anlage
 * gerechnet — Stringpruefung und Materialliste.
 *
 * Wie schon bei der Belegung gilt: gespeichert ist nur die Eingabe. Die
 * Materialliste wird nie abgelegt, sondern jedes Mal aus Belegung, Komponenten,
 * Leitungswegen und Materialsatz gerechnet. Nur die von Hand ergaenzten Zeilen
 * liegen in der Datenbank (PV-J06). Sonst haette man zwei Wahrheiten, und die
 * laufen auseinander, sobald jemand ein Modul entfernt.
 */

import {
  belegungsverhaeltnis,
  modulstraenge,
  nachGrundriss,
  pruefeAnlage,
  stringleitung,
  dcSpannungsfall,
  acSpannungsfall,
  DC_GRENZE_PROZENT,
  DEFAULT_AUSLEGUNG,
  type Auslegung,
  type Stringleitung,
  type Modulelektrik,
  type Punkt,
  rechneMaterial,
  type Anlagenbefund,
  type Artikel as Kernartikel,
  type Kalkulationsregeln,
  type Komponentenbedarf,
  type Leitungsbedarf,
  type Materialliste,
  type Materialsatz as Kernmaterialsatz,
  type Modulstring,
  type Position,
  type Warengruppe,
} from '@pv/geometrie';

import { traegerFuer, type Planung } from './planung.ts';
import type {
  Artikel,
  Belegung,
  Flaeche,
  Komponente,
  Leitungsweg,
  Materialposition,
  Materialsatz,
  Modul,
  Projektkomponente,
  Stringzeile,
  Wechselrichterdaten,
} from './typen.ts';

export interface Anlageneingabe {
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  belegungen: Belegung[];
  module: Modul[];
  strings: Stringzeile[];
  projektkomponenten: Projektkomponente[];
  komponenten: Komponente[];
  artikel: Artikel[];
  materialsaetze: Materialsatz[];
  leitungswege: Leitungsweg[];
  materialpositionen: Materialposition[];
  kalkulation: Kalkulationsregeln;
  /**
   * Preise, die nur fuer dieses Projekt gelten (PV-J07): Sonderkondition,
   * Restposten, Kunde stellt bei. Schluessel ist die Artikel- oder
   * Modulkennung.
   */
  preisueberschreibungen?: Record<string, number>;
  /** Welche Artikel die DC-Leitung braucht, und die Vorgaben dafuer. */
  stringleitung?: {
    solarleitungArtikelId: string | null;
    steckerpaarArtikelId: string | null;
    anschlussleitungM: number;
    zuleitungM: number;
  };
  /**
   * Temperaturen der Auslegung (Einstellung auslegung.temperatur). Ohne
   * Angabe gelten −10 und 70 °C — vorher galten die immer, egal was
   * eingestellt war.
   */
  auslegung?: Auslegung;
  /** Welche Artikel fuer Leitungswege und Anbindungen (Einstellung material.leitungsartikel). */
  leitungsartikel?: Partial<
    Record<
      | 'acKabel'
      | 'rohrAlu'
      | 'rohrEdelstahl'
      | 'dcGeschirmt'
      | 'dcErdkabel'
      | 'acErdkabel'
      | 'netzwerkkabel'
      | 'steuerleitung',
      string
    >
  >;
}

export interface Spannungsfallzeile {
  bezeichnung: string;
  prozent: number;
  zulaessig: boolean;
  grenzeProzent: number;
  /** Was angenommen wurde, weil es fehlte — steht dabei, damit niemand der Zahl blind traut. */
  annahme?: string;
}

export interface Anlagendaten {
  befund: Anlagenbefund;
  material: Materialliste;
  leistungKwp: number;
  /** Der erste Wechselrichter am Projekt — nach ihm richtet sich die Pruefung. */
  wechselrichter?: { projektkomponente: Projektkomponente; stammdaten: Komponente };
  verhaeltnis?: { verhaeltnis: number; hinweis?: string };
  speicherKwh: number;
  /** DC-Leitung je String, nach String-Kennung. */
  stringleitungen: Map<string, Stringleitung>;
  /** Spannungsabfall je Leitungsweg, nach Kennung des Wegs. */
  spannungsfaelle: Map<string, Spannungsfallzeile[]>;
  /**
   * Steht bei jedem verwendeten Modul die höchste Systemspannung? Ohne sie
   * bleibt der Punkt im Prüfbefund offen, statt still als bestanden zu gelten.
   */
  systemspannungBekannt: boolean;
  /** Mit welchen Temperaturen geprüft wurde — für Ausdrucke und Beschreibung. */
  auslegung: Auslegung;
}

export function rechneAnlage(eingabe: Anlageneingabe): Anlagendaten {
  // Gelöschte Komponenten zählen nicht — vorher galt ein entfernter
  // Wechselrichter weiter als der des Projekts, und ein entfernter Speicher
  // stand in der Kapazität.
  const e = { ...eingabe, projektkomponenten: eingabe.projektkomponenten.filter((pk) => !pk.geloescht_am) };
  const auslegung = e.auslegung ?? DEFAULT_AUSLEGUNG;
  const stammdatenZu = (id: string | null): Komponente | undefined =>
    id ? e.komponenten.find((k) => k.id === id) : undefined;

  // --- Wechselrichter ------------------------------------------------------
  let wechselrichter: Anlagendaten['wechselrichter'];
  for (const pk of e.projektkomponenten) {
    const stamm = stammdatenZu(pk.komponente_id);
    if (stamm?.art === 'wechselrichter') {
      wechselrichter = { projektkomponente: pk, stammdaten: stamm };
      break;
    }
  }
  const wrDaten = wechselrichter?.stammdaten.kenndaten as Partial<Wechselrichterdaten> | undefined;

  const speicherKwh = e.projektkomponenten.reduce((summe, pk) => {
    const stamm = stammdatenZu(pk.komponente_id);
    if (stamm?.art !== 'speicher') return summe;
    return summe + (stamm.kenndaten.kapazitaetKwh ?? 0) * pk.anzahl;
  }, 0);

  // --- Verstringung --------------------------------------------------------
  const vorhandeneModule = new Map<string, Set<string>>();
  const azimutJeFlaeche = new Map<string, number>();
  const modulJeFlaeche = new Map<string, Modulelektrik>();
  let leistungKwp = 0;

  for (const flaeche of e.flaechen) {
    const planung = e.planungen.get(flaeche.id);
    if (!planung) continue;
    vorhandeneModule.set(
      flaeche.id,
      new Set(planung.gesetzt.map((platz) => `${platz.reihe}:${platz.spalte}`)),
    );
    azimutJeFlaeche.set(flaeche.id, planung.kennzahlen.azimutGrad);
    leistungKwp += planung.zahlen.leistungKwp;

    const belegung = e.belegungen.find((b) => b.flaeche_id === flaeche.id && !b.geloescht_am);
    const modul = belegung?.modul_id ? e.module.find((m) => m.id === belegung.modul_id) : undefined;
    if (modul) {
      modulJeFlaeche.set(flaeche.id, {
        uocV: Number(modul.uoc_v),
        tkUocProzentProK: Number(modul.tk_uoc_prozent_k),
        ...(modul.umpp_v !== null ? { umppV: Number(modul.umpp_v) } : {}),
        ...(modul.impp_a != null ? { imppA: Number(modul.impp_a) } : {}),
        ...(modul.max_systemspannung_v != null
          ? { maxSystemspannungV: Number(modul.max_systemspannung_v) }
          : {}),
      });
    }
  }

  const kernstrings: Modulstring[] = e.strings
    .filter((s) => !s.geloescht_am)
    .map((s) => ({
      id: s.id,
      name: s.name,
      farbe: s.farbe,
      module: s.module.map((m) => ({ flaecheId: m.flaecheId, reihe: m.reihe, spalte: m.spalte })),
      ...(s.mppt !== null ? { mppt: s.mppt } : {}),
    }));

  const befund = pruefeAnlage({
    strings: kernstrings,
    vorhandeneModule,
    azimutJeFlaeche,
    modulJeFlaeche,
    ...(wrDaten?.maxEingangsspannungV
      ? {
          wechselrichter: {
            maxEingangsspannungV: wrDaten.maxEingangsspannungV,
            ...(wrDaten.mppUntergrenzeV !== undefined
              ? { mppUntergrenzeV: wrDaten.mppUntergrenzeV }
              : {}),
            anzahlMppt: wrDaten.anzahlMppt ?? 1,
            acLeistungKw: wrDaten.acLeistungKw ?? 0,
            ...(wrDaten.minEingangsspannungV !== undefined
              ? { minEingangsspannungV: wrDaten.minEingangsspannungV }
              : {}),
            ...(wrDaten.maxStromJeMpptA?.length
              ? { maxStromJeMpptA: wrDaten.maxStromJeMpptA }
              : {}),
            ...(wrDaten.maxPvLeistungKw !== undefined
              ? { maxPvLeistungKw: wrDaten.maxPvLeistungKw }
              : {}),
          },
        }
      : {}),
    leistungKwp,
    auslegung,
  });

  // --- Material ------------------------------------------------------------
  const ueberschrieben = e.preisueberschreibungen ?? {};
  const preisFuer = (id: string, allgemein: number | null | undefined): number | undefined => {
    const eigener = ueberschrieben[id];
    if (eigener !== undefined && Number.isFinite(eigener)) return eigener;
    return allgemein == null ? undefined : Number(allgemein);
  };

  const artikelKarte = new Map<string, Kernartikel>(
    e.artikel.map((a) => {
      const preis = preisFuer(a.id, a.einkaufspreis);
      return [
        a.id,
        {
          id: a.id,
          bezeichnung: a.bezeichnung,
          einheit: a.einheit,
          ...(preis !== undefined ? { einkaufspreis: preis } : {}),
          warengruppe: a.warengruppe as Warengruppe,
        },
      ];
    }),
  );

  // Module stehen im Modulpool, nicht im Artikelstamm — die Belegung zeigt auf
  // eine Modul-Kennung. Damit die Materialliste sie benennen und bepreisen
  // kann, kommen sie hier als Artikel dazu. Ohne das steht im Angebot
  // "Unbekannter Artikel" ohne Betrag.
  for (const modul of e.module) {
    const preis = preisFuer(modul.id, modul.einkaufspreis);
    artikelKarte.set(modul.id, {
      id: modul.id,
      bezeichnung: `${modul.hersteller} ${modul.typ}`,
      einheit: 'Stk',
      ...(preis !== undefined ? { einkaufspreis: preis } : {}),
      warengruppe: 'module',
    });
  }

  const komponentenbedarf: Komponentenbedarf[] = e.projektkomponenten
    .filter((pk) => !pk.geloescht_am)
    .map((pk) => {
      const stamm = stammdatenZu(pk.komponente_id);
      return {
        bezeichnung: stamm ? `${stamm.hersteller} ${stamm.typ}` : 'Komponente',
        anzahl: pk.anzahl,
        ...(stamm ? { einzelpreis: preisFuer(stamm.id, stamm.einkaufspreis) } : {}),
        warengruppe: (stamm?.warengruppe ?? 'sonstiges') as Warengruppe,
      };
    });

  // --- Leitungswege ------------------------------------------------------
  // Der DC-Weg beschreibt den Weg vom Dach zum Wechselrichter. Das Kabel
  // darin rechnen die Strings (zweimal je String); hier kommt nur dazu, was
  // einmal entlang des Wegs liegt: das Schutzrohr. Vorher zaehlte der DC-Weg
  // sein Kabel selbst und die Strings ihre Zuleitung noch einmal.
  // Der AC-Weg ist ein mehradriges Kabel — Laenge einmal, plus Rohr.
  const la = e.leitungsartikel ?? {};
  const offeneWege = e.leitungswege.filter((l) => !l.geloescht_am);
  const dcWeg = offeneWege.find((l) => (l.art ?? (l.von === 'PV-Generator' ? 'dc' : 'ac')) === 'dc');
  const leitungsbedarf: Leitungsbedarf[] = [];
  for (const l of offeneWege) {
    const art = l.art ?? (l.von === 'PV-Generator' ? 'dc' : 'ac');
    const laenge = Number(l.laenge_m);
    if (!(laenge > 0)) continue;
    const name = l.beschreibung?.trim() || `${l.von} bis ${l.nach}`;
    if (art === 'ac') {
      const kabel = l.artikel_id ?? (l.verlegeart === 'erdkabel' ? la.acErdkabel : la.acKabel);
      leitungsbedarf.push({ bezeichnung: `AC-Leitung ${name}`, laengeM: laenge, ...(kabel ? { artikelId: kabel } : {}) });
    }
    const rohr = l.verlegeart === 'rohr_alu' ? la.rohrAlu : l.verlegeart === 'rohr_edelstahl' ? la.rohrEdelstahl : undefined;
    if (rohr) leitungsbedarf.push({ bezeichnung: `Schutzrohr ${name}`, laengeM: laenge, artikelId: rohr });
  }

  // Anbindung von Smart-Meter, WLV und Internet: Leitung bis zum Wechselrichter.
  for (const pk of e.projektkomponenten.filter((k) => !k.geloescht_am)) {
    const stamm = stammdatenZu(pk.komponente_id);
    const angaben = pk.angaben ?? {};
    if (!stamm) continue;
    if ((stamm.art === 'smartmeter' || stamm.art === 'wlv_zk' || stamm.art === 'wlv_extern') && angaben.entfernungZuWrM) {
      const artikel = stamm.art === 'smartmeter' ? la.netzwerkkabel : la.steuerleitung;
      leitungsbedarf.push({
        bezeichnung: `${stamm.art === 'smartmeter' ? 'Netzwerkkabel Smart-Meter' : 'Steuerleitung WLV'}`,
        laengeM: Number(angaben.entfernungZuWrM),
        ...(artikel ? { artikelId: artikel } : {}),
      });
    }
    if (stamm.art === 'internet' && angaben.anbindung === 'kabel' && angaben.kabelM) {
      leitungsbedarf.push({
        bezeichnung: 'Netzwerkkabel Internetanschluss',
        laengeM: Number(angaben.kabelM),
        ...(la.netzwerkkabel ? { artikelId: la.netzwerkkabel } : {}),
      });
    }
  }

  const kernsaetze: Kernmaterialsatz[] = e.materialsaetze
    .filter((s) => s.aktiv)
    .map((s) => ({
      dachtyp: s.dachtyp,
      ausrichtung: s.ausrichtung,
      jeModul: s.je_modul,
      jeFlaeche: s.je_flaeche,
      montage: s.montage ?? null,
    }));

  const manuelle: Position[] = e.materialpositionen
    .filter((p) => !p.geloescht_am)
    .map((p) => ({
      ...(p.artikel_id ? { artikelId: p.artikel_id } : {}),
      bezeichnung: p.bezeichnung,
      einheit: p.einheit,
      menge: Number(p.menge),
      ...(p.einzelpreis !== null ? { einzelpreis: Number(p.einzelpreis) } : {}),
      warengruppe: p.warengruppe as Warengruppe,
      herkunft: 'manuell' as const,
    }));

  // --- DC-Leitung je String ------------------------------------------------
  // Modulmitten in Stringreihenfolge. Liegt der String auf einer Flaeche — der
  // Normalfall —, wird in der Dachebene gemessen, also mit dem Gefaelle. Ueber
  // mehrere Flaechen hinweg im Grundriss; dort ist es ohnehin eine Schaetzung.
  const leitungsvorgabe = e.stringleitung ?? {
    solarleitungArtikelId: null,
    steckerpaarArtikelId: null,
    anschlussleitungM: 1.2,
    zuleitungM: 15,
  };
  const stringleitungen = new Map<string, Stringleitung>();
  for (const s of e.strings.filter((z) => !z.geloescht_am)) {
    const plaetze = s.module.map((m) => {
      const planung = e.planungen.get(m.flaecheId);
      const index = planung?.gesetzt.findIndex((p) => p.reihe === m.reihe && p.spalte === m.spalte) ?? -1;
      return planung && index >= 0
        ? { flaecheId: m.flaecheId, mitte: planung.gesetzt[index]!.mitte, geo: planung.moduleGeo[index] ?? [] }
        : null;
    });
    const gueltige = plaetze.filter((p): p is NonNullable<typeof p> => p !== null);
    const eineFlaeche = new Set(gueltige.map((p) => p.flaecheId)).size <= 1;
    let mitten: Punkt[];
    // Fassadenmodule liegen nicht auf der Karte und haben kein Luftbild-Polygon
    // — dann bleibt es bei den Mitten in der Ebene, statt am Leeren zu reißen.
    if (eineFlaeche || gueltige.some((p) => p.geo.length === 0)) {
      mitten = gueltige.map((p) => p.mitte);
    } else {
      const geoMitte = (ecken: Array<{ lat: number; lng: number }>) => ({
        lat: ecken.reduce((a, p) => a + p.lat, 0) / ecken.length,
        lng: ecken.reduce((a, p) => a + p.lng, 0) / ecken.length,
      });
      const bezug = geoMitte(gueltige[0]!.geo);
      mitten = gueltige.map((p) => nachGrundriss(geoMitte(p.geo), bezug));
    }
    stringleitungen.set(
      s.id,
      stringleitung({
        mitten,
        anschlussleitungM: leitungsvorgabe.anschlussleitungM,
        // Zuleitung: am String eingetragen, sonst die Laenge des DC-Wegs,
        // sonst die Vorgabe.
        zuleitungM: Number(
          s.zuleitung_m ?? (dcWeg && Number(dcWeg.laenge_m) > 0 ? dcWeg.laenge_m : leitungsvorgabe.zuleitungM),
        ),
      }),
    );
  }
  // Auf dem Dach (Rueckfuehrung, Verlaengerungen) liegt Solarleitung; die
  // Zuleitung haengt an der Verlegeart des DC-Wegs.
  const zuleitungsartikel =
    dcWeg?.verlegeart === 'geschirmt'
      ? la.dcGeschirmt
      : dcWeg?.verlegeart === 'erdkabel'
        ? la.dcErdkabel
        : leitungsvorgabe.solarleitungArtikelId ?? undefined;
  const stringleitungsbedarf: Leitungsbedarf[] = [];
  for (const s of e.strings.filter((z) => !z.geloescht_am)) {
    const l = stringleitungen.get(s.id);
    if (!l || l.gesamtM <= 0) continue;
    const dach = Math.round((l.rueckfuehrungM + l.verlaengerungenM) * 10) / 10;
    const zu = Math.round(l.zuleitungM * 10) / 10;
    if (dach > 0) {
      stringleitungsbedarf.push({
        bezeichnung: `DC-Leitung ${s.name} am Dach`,
        laengeM: dach,
        ...(leitungsvorgabe.solarleitungArtikelId ? { artikelId: leitungsvorgabe.solarleitungArtikelId } : {}),
      });
    }
    if (zu > 0) {
      stringleitungsbedarf.push({
        bezeichnung: `DC-Leitung ${s.name} zum Wechselrichter`,
        laengeM: zu,
        ...(zuleitungsartikel ? { artikelId: zuleitungsartikel } : {}),
      });
    }
  }
  const steckerpaare = [...stringleitungen.values()].reduce((summe, l) => summe + l.steckerpaare, 0);

  // --- Spannungsabfall (Wunsch von Mathias, 11/2026) ------------------------
  const spannungsfaelle = new Map<string, Spannungsfallzeile[]>();
  for (const l of offeneWege) {
    const art = l.art ?? (l.von === 'PV-Generator' ? 'dc' : 'ac');
    const annahmeQ = l.querschnitt_mm2 == null;
    const querschnitt = Number(l.querschnitt_mm2 ?? (art === 'dc' ? 6 : 6));
    const zeilen: Spannungsfallzeile[] = [];
    if (art === 'dc') {
      for (const s of e.strings.filter((z) => !z.geloescht_am)) {
        const leitung = stringleitungen.get(s.id);
        const erstes = s.module[0];
        const elektrik = erstes ? modulJeFlaeche.get(erstes.flaecheId) : undefined;
        if (!leitung || !elektrik?.umppV || !elektrik.imppA || s.module.length === 0) continue;
        const r = dcSpannungsfall({
          schleifeM: leitung.gesamtM,
          stromA: elektrik.imppA,
          spannungV: elektrik.umppV * s.module.length,
          querschnittMm2: querschnitt,
        });
        if (r) {
          zeilen.push({
            bezeichnung: s.name,
            prozent: r.prozent,
            zulaessig: r.zulaessig,
            grenzeProzent: DC_GRENZE_PROZENT,
            ...(annahmeQ ? { annahme: '6 mm² angenommen' } : {}),
          });
        }
      }
    } else if (wrDaten?.acLeistungKw) {
      const spannung = Number(l.spannung_v ?? 400);
      const r = acSpannungsfall({
        laengeM: Number(l.laenge_m),
        leistungW: wrDaten.acLeistungKw * 1000,
        spannungV: spannung,
        phasen: spannung <= 250 ? 1 : 3,
        querschnittMm2: querschnitt,
      });
      if (r) {
        zeilen.push({
          bezeichnung: `${wrDaten.acLeistungKw} kW bei ${spannung} V`,
          prozent: r.prozent,
          zulaessig: r.zulaessig,
          grenzeProzent: 3,
          ...(annahmeQ ? { annahme: '6 mm² angenommen' } : {}),
        });
      }
    }
    spannungsfaelle.set(l.id, zeilen);
  }

  const material = rechneMaterial({
    flaechen: e.flaechen.map((flaeche) => {
      const belegung = e.belegungen.find((b) => b.flaeche_id === flaeche.id && !b.geloescht_am);
      const planung = e.planungen.get(flaeche.id);
      const modul = belegung?.modul_id ? e.module.find((m) => m.id === belegung.modul_id) : undefined;
      const ausrichtung = belegung?.ausrichtung ?? 'hoch';
      return {
        flaecheId: flaeche.id,
        name: flaeche.name,
        dachtyp: flaeche.dachtyp ?? '',
        ausrichtung,
        anzahlModule: planung?.zahlen.anzahl ?? 0,
        ...(belegung?.modul_id ? { modulArtikelId: belegung.modul_id } : {}),
        // Klemmen und Schienen haengen an der Lage der Module, nicht an ihrer
        // Zahl — ein entferntes Modul mitten in der Reihe teilt den Strang.
        ...(planung && modul && belegung
          ? {
              straenge: modulstraenge(
                planung.gesetzt,
                ausrichtung,
                { breiteMm: modul.breite_mm, hoeheMm: modul.hoehe_mm },
                belegung.regeln,
              ),
            }
          : {}),
        // Sind Sparren oder Pfetten eingetragen, sitzen die Haken darauf.
        ...(() => {
          const traeger = traegerFuer(flaeche);
          return traeger ? { traeger: traeger.lagen } : {};
        })(),
      };
    }),
    komponenten: komponentenbedarf,
    leitungen: [...stringleitungsbedarf, ...leitungsbedarf],
    materialsaetze: kernsaetze,
    artikel: artikelKarte,
    ...(leitungsvorgabe.steckerpaarArtikelId && steckerpaare > 0
      ? { stueckgut: [{ artikelId: leitungsvorgabe.steckerpaarArtikelId, menge: steckerpaare }] }
      : {}),
    manuellePositionen: manuelle,
    regeln: e.kalkulation,
  });

  return {
    befund,
    material,
    leistungKwp,
    ...(wechselrichter ? { wechselrichter } : {}),
    ...(wrDaten?.acLeistungKw
      ? { verhaeltnis: belegungsverhaeltnis(leistungKwp, wrDaten.acLeistungKw) }
      : {}),
    speicherKwh,
    stringleitungen,
    spannungsfaelle,
    systemspannungBekannt:
      modulJeFlaeche.size > 0 && [...modulJeFlaeche.values()].every((m) => m.maxSystemspannungV != null),
    auslegung,
  };
}

/**
 * Modulfarben fuer die Karte: welcher Platz gehoert zu welchem String (PV-H02).
 * Module ohne String bleiben ungefaerbt und fallen dadurch auf.
 */
export function modulfarben(strings: Stringzeile[]): Map<string, string> {
  const farben = new Map<string, string>();
  for (const string of strings) {
    if (string.geloescht_am) continue;
    for (const modul of string.module) {
      farben.set(`${modul.flaecheId}|${modul.reihe}:${modul.spalte}`, string.farbe);
    }
  }
  return farben;
}
